/**
 * Seleção do elemento reclamado.
 *
 * Reescrito a partir do picker do Sensa, sem React e sem depender de nada da
 * página do cliente. O objetivo é o mesmo: quem reporta aponta o que está
 * errado, e o time recebe um seletor CSS que localiza aquilo no código.
 */

import type { PickedElement } from './types';
import { WIDGET_Z_INDEX } from './styles';

const MAX_TEXT_LENGTH = 160;
const MAX_SELECTOR_DEPTH = 4;

function escapeSelector(value: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(value);
  return value.replace(/([!"#$%&'()*+,./:;<=>?@[\\\]^`{|}~])/g, '\\$1');
}

/**
 * Monta um seletor que localize o elemento no código do cliente.
 *
 * A ordem importa: `data-clixy-id` é o que o cliente define de propósito para
 * este fim, `data-testid` costuma existir e sobreviver a refatoração, `id` é
 * estável mas às vezes gerado. O caminho por `nth-child` é o último recurso,
 * porque quebra quando a árvore muda.
 */
export function buildSelector(element: Element): string {
  const clixyId = element.getAttribute('data-clixy-id');
  if (clixyId) return `[data-clixy-id="${clixyId}"]`;

  const testId = element.getAttribute('data-testid');
  if (testId) return `[data-testid="${testId}"]`;

  const id = element.getAttribute('id');
  if (id) return `#${escapeSelector(id)}`;

  const parts: string[] = [];
  let current: Element | null = element;

  for (let depth = 0; depth < MAX_SELECTOR_DEPTH && current && current !== document.body; depth++) {
    const tag = current.tagName.toLowerCase();
    const parent: Element | null = current.parentElement;

    if (!parent) {
      parts.unshift(tag);
      break;
    }

    const index = Array.from(parent.children).indexOf(current) + 1;
    parts.unshift(`${tag}:nth-child(${index})`);
    current = parent;
  }

  return parts.join(' > ');
}

function describe(element: Element): PickedElement {
  const rect = element.getBoundingClientRect();

  return {
    selector: buildSelector(element),
    tag: element.tagName.toLowerCase(),
    text: (element.textContent ?? '').trim().slice(0, MAX_TEXT_LENGTH) || null,
    aria_label: element.getAttribute('aria-label'),
    test_id: element.getAttribute('data-testid') ?? element.getAttribute('data-clixy-id'),
    rect: { x: rect.left, y: rect.top, w: rect.width, h: rect.height },
  };
}

interface PickerOptions {
  hint: string;
  accentColor: string;
  onPick: (element: PickedElement | null) => void;
}

/** Ativa o modo de seleção. Devolve a função que o desliga. */
export function activatePicker({ hint, accentColor, onPick }: PickerOptions): () => void {
  const banner = document.createElement('div');
  banner.textContent = hint;
  banner.setAttribute('data-clixy-ignore', 'true');
  banner.style.cssText = `
    position: fixed; top: 16px; left: 50%; transform: translateX(-50%);
    z-index: ${WIDGET_Z_INDEX + 2}; background: #16151f; color: #ffffff;
    padding: 10px 18px; border-radius: 999px; pointer-events: none;
    font: 500 13px/1.4 system-ui, -apple-system, sans-serif;
    box-shadow: 0 12px 32px rgba(0,0,0,0.28); max-width: 90vw; text-align: center;
  `;

  const highlight = document.createElement('div');
  highlight.setAttribute('data-clixy-ignore', 'true');

  // O realce precisa aparecer sobre qualquer fundo, porque o widget roda na
  // página de terceiro e não sabe se o elemento sob o cursor é claro ou escuro.
  // Só a cor de destaque não basta: sobre um botão escuro ela some.
  //
  // Daí as três camadas: o preenchimento tinge a área, o anel branco por fora da
  // borda a separa de fundo escuro, e o brilho externo dá presença em fundo
  // claro. Uma delas sempre pega.
  highlight.style.cssText = `
    position: fixed; z-index: ${WIDGET_Z_INDEX + 1}; pointer-events: none;
    border: 2px solid ${accentColor}; border-radius: 6px;
    background: ${accentColor}38;
    box-shadow:
      0 0 0 1px rgba(255,255,255,0.9),
      inset 0 0 0 1px rgba(255,255,255,0.55),
      0 0 0 6px ${accentColor}30;
    transition: all 60ms linear;
  `;

  const cursorStyle = document.createElement('style');
  cursorStyle.textContent = '*, *::before, *::after { cursor: crosshair !important; }';

  document.body.append(banner, highlight);
  document.head.append(cursorStyle);

  let lastTarget: Element | null = null;

  function resolveTarget(event: MouseEvent): Element | null {
    const target = document.elementFromPoint(event.clientX, event.clientY);
    if (!target) return null;
    // Ignora os próprios adornos do picker e o host do widget.
    if (target.closest('[data-clixy-ignore], #clixy-widget-root')) return null;
    return target;
  }

  function onMove(event: MouseEvent) {
    const target = resolveTarget(event);
    if (!target || target === lastTarget) return;

    lastTarget = target;
    const rect = target.getBoundingClientRect();
    highlight.style.left = `${rect.left}px`;
    highlight.style.top = `${rect.top}px`;
    highlight.style.width = `${rect.width}px`;
    highlight.style.height = `${rect.height}px`;
  }

  function cleanup() {
    document.removeEventListener('mousemove', onMove, true);
    document.removeEventListener('click', onClick, true);
    document.removeEventListener('keydown', onKey, true);
    banner.remove();
    highlight.remove();
    cursorStyle.remove();
  }

  function onClick(event: MouseEvent) {
    // Captura na fase de captura e cancela: sem isto, o clique dispararia a ação
    // real do elemento e a pessoa navegaria para outra página em vez de apontar.
    event.preventDefault();
    event.stopPropagation();

    const target = resolveTarget(event);
    if (!target) return;

    cleanup();
    onPick(describe(target));
  }

  function onKey(event: KeyboardEvent) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    cleanup();
    onPick(null);
  }

  document.addEventListener('mousemove', onMove, true);
  document.addEventListener('click', onClick, true);
  document.addEventListener('keydown', onKey, true);

  return cleanup;
}
